import React, { useCallback, useState } from "react";
import { Alert, Modal, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { verifyPasscode } from "../utils/screenTimePasscode.js";

/**
 * Per-edit passcode gate — re-prompts before each Screen Time change when a passcode is set.
 *
 * @param {{ hasPasscode: boolean, passcodeSalt: string, passcodeHash: string }} config
 */
export function useScreenTimeEditGuard({ hasPasscode, passcodeSalt, passcodeHash }) {
  const [visible, setVisible] = useState(false);
  const [pin, setPin] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const guardEdit = useCallback(
    (action) => {
      if (!hasPasscode) {
        void Promise.resolve(action());
        return;
      }
      setPending(() => action);
      setPin("");
      setVisible(true);
    },
    [hasPasscode],
  );

  const confirm = async () => {
    if (!pin.trim()) {
      Alert.alert("Passcode required", "Enter your Screen Time passcode to make this change.");
      return;
    }
    setBusy(true);
    try {
      const ok = await verifyPasscode(pin.trim(), passcodeSalt, passcodeHash);
      if (!ok) {
        Alert.alert("Wrong passcode", "That passcode did not match. Try again.");
        return;
      }
      setVisible(false);
      setPin("");
      const run = pending;
      setPending(null);
      await Promise.resolve(run?.());
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setVisible(false);
    setPin("");
    setPending(null);
  };

  const modal = (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={cancel}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>Confirm passcode</Text>
          <Text style={styles.sub}>
            Screen Time changes are protected. Enter your passcode to continue — this helps prevent bypassing your
            limits.
          </Text>
          <TextInput
            value={pin}
            onChangeText={setPin}
            placeholder="••••"
            placeholderTextColor="#94A3B8"
            style={styles.input}
            secureTextEntry
            keyboardType="number-pad"
            maxLength={12}
            autoFocus
          />
          <Pressable style={[styles.btn, styles.btnPrimary]} onPress={() => void confirm()} disabled={busy}>
            <Text style={styles.btnText}>{busy ? "Checking…" : "Confirm & change"}</Text>
          </Pressable>
          <Pressable style={[styles.btn, styles.btnCancel]} onPress={cancel}>
            <Text style={styles.btnCancelText}>Cancel</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );

  return { guardEdit, editPasscodeModal: modal };
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 20,
  },
  title: { fontSize: 20, fontWeight: "800", color: "#0F172A", marginBottom: 8 },
  sub: { fontSize: 13, color: "#64748B", lineHeight: 19, marginBottom: 16 },
  input: {
    borderWidth: 1,
    borderColor: "#CBD5E1",
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: "#F8FAFC",
    marginBottom: 12,
  },
  btn: { paddingVertical: 13, borderRadius: 10, alignItems: "center", marginTop: 6 },
  btnPrimary: { backgroundColor: "#0D9488" },
  btnText: { color: "#fff", fontWeight: "800", fontSize: 15 },
  btnCancel: { backgroundColor: "transparent" },
  btnCancelText: { color: "#64748B", fontWeight: "700" },
});
